import { useActivityTracker } from '@/features/activity/useActivityTracker';
import { RevealGate } from '@/features/newDesignReveal/RevealGate';
import { cn } from '@/lib/cn';
import { useEffect, useRef } from 'react';
import {
  Outlet,
  ScrollRestoration,
  useLocation,
  useMatches,
  useNavigationType,
} from 'react-router-dom';
import { SectionTabs, type SectionTabsGroup } from './SectionTabs';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

/**
 * Rama aplikacji (redesign 2026-09-30).
 * < lg: przewija sie cala strona (iOS Safari zle przewija elementy wewnatrz
 *   overflow:hidden), pasek gorny przyklejony, dolny pasek nawigacji —
 *   tresc ma zapas na dole (pb) na pasek + safe area.
 * >= lg: sidebar + pasek staly, przewija sie <main> (kanban potrzebuje
 *   ograniczonej wysokosci).
 *
 * Przewijanie po zmianie strony (Dawid 2026-09-30: „przewijam do polowy listy,
 * klikam leada i widze jego dol — RODO”): nowa strona zawsze od gory,
 * „wstecz” wraca w to samo miejsce listy. Okno obsluguje ScrollRestoration,
 * <main> na desktopie zerujemy recznie.
 */
export function DashboardLayout() {
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  useActivityTracker();
  // Tylko kanban (handle.fullHeight) dostaje sztywna wysokosc. Inaczej dolny odstep
  // (pb) liczyl sie od wysokosci okna, a nie od konca tresci — dlugie strony
  // konczyly sie kartą przyklejona do dolu (Dawid 2026-09-30, widok oferty).
  const matches = useMatches();
  const fullHeight = matches.some(
    (m) => (m.handle as { fullHeight?: boolean } | undefined)?.fullHeight === true,
  );
  // Zakładki sekcji (Ustawienia / Raporty / Narzędzia) — trasa zgłasza grupę w handle.tabs.
  const tabsGroup = matches
    .map((m) => (m.handle as { tabs?: SectionTabsGroup } | undefined)?.tabs)
    .find((g) => g !== undefined);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname = wyzwalacz (nowa strona)
  useEffect(() => {
    if (navigationType !== 'POP') mainRef.current?.scrollTo(0, 0);
  }, [pathname, navigationType]);

  return (
    <div className="flex bg-bg lg:h-screen lg:overflow-hidden">
      {/* Jednorazowa rozbiórka starego wyglądu młotkiem (nowa wersja). */}
      <RevealGate />
      <Sidebar />
      <div className="flex w-full min-w-0 flex-1 flex-col lg:overflow-hidden">
        <Topbar />
        <main ref={mainRef} className="flex-1 lg:overflow-y-auto">
          <div
            className={cn(
              'mx-auto max-w-[1600px] px-4 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-5 sm:px-6 md:pt-7 lg:px-8 lg:pb-10',
              fullHeight && 'lg:h-full',
            )}
          >
            {tabsGroup && <SectionTabs group={tabsGroup} />}
            <Outlet />
          </div>
        </main>
      </div>
      <ScrollRestoration />
    </div>
  );
}
